import React from 'react';
import { ChangeEvent } from 'react';
import { Box, FormControlLabel, FormGroup, Stack, Typography } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { IconMicrophone, IconPhoneIncoming, IconPhoneOff } from '@tabler/icons-react';

import CustomSwitch from '@shared/components/forms/theme-elements/CustomSwitch';
import { AgentToolsFormValue } from '@features/Admin/Agent/model/types';
import { StaticSettingsBlock } from './SettingsBlocks';

type Props = { isReadOnly: boolean };

export const CallLifecycleSettingsPanel: React.FC<Props> = ({ isReadOnly }) => {
  const { t } = useTranslation();
  const { control } = useFormContext<AgentToolsFormValue>();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <StaticSettingsBlock
        header={
          <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
            <Stack direction="row" alignItems="center" spacing={1}>
              <IconPhoneIncoming width={16} />
              <Typography fontSize="16px" fontWeight={600}>
                {t('campaignPage.callbackRequestCheckbox')}
              </Typography>
            </Stack>
            <FormGroup>
              <FormControlLabel
                sx={{ marginRight: 0 }}
                control={
                  <Controller
                    name="callbackRequestTool.enabled"
                    control={control}
                    render={({ field }) => (
                      <CustomSwitch
                        {...field}
                        checked={Boolean(field.value)}
                        disabled={isReadOnly}
                        inputProps={{
                          'aria-label': t('campaignPage.callbackRequestCheckbox'),
                        }}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          field.onChange(e.target.checked);
                        }}
                      />
                    )}
                  />
                }
                label=""
              />
            </FormGroup>
          </Stack>
        }
      />

      <StaticSettingsBlock
        header={
          <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
            <Stack direction="row" alignItems="center" spacing={1}>
              <IconMicrophone width={16} />
              <Typography fontSize="16px" fontWeight={600}>
                {t('campaignPage.voiceMailDetectionCheckbox')}
              </Typography>
            </Stack>
            <FormGroup>
              <FormControlLabel
                sx={{ marginRight: 0 }}
                onClick={(event: React.MouseEvent) => event.stopPropagation()}
                control={
                  <Controller
                    name="voiceMailDetectionTool.enabled"
                    control={control}
                    render={({ field }) => (
                      <CustomSwitch
                        {...field}
                        checked={Boolean(field.value)}
                        disabled={isReadOnly}
                        inputProps={{
                          'aria-label': t('campaignPage.voiceMailDetectionCheckbox'),
                        }}
                        onClick={(event: React.MouseEvent) => event.stopPropagation()}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          field.onChange(e.target.checked);
                        }}
                      />
                    )}
                  />
                }
                label=""
              />
            </FormGroup>
          </Stack>
        }
      />

      <StaticSettingsBlock
        header={
          <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
            <Stack direction="row" alignItems="center" spacing={1}>
              <IconPhoneOff width={16} />
              <Typography fontSize="16px" fontWeight={600}>
                {t('campaignPage.endCallCheckbox')}
              </Typography>
            </Stack>
            <FormGroup>
              <FormControlLabel
                sx={{ marginRight: 0 }}
                onClick={(event: React.MouseEvent) => event.stopPropagation()}
                control={
                  <Controller
                    name="endCallTool.enabled"
                    control={control}
                    render={({ field }) => (
                      <CustomSwitch
                        {...field}
                        checked={Boolean(field.value)}
                        disabled={isReadOnly}
                        inputProps={{
                          'aria-label': t('campaignPage.endCallCheckbox'),
                        }}
                        onClick={(event: React.MouseEvent) => event.stopPropagation()}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          field.onChange(e.target.checked);
                        }}
                      />
                    )}
                  />
                }
                label=""
              />
            </FormGroup>
          </Stack>
        }
      />
    </Box>
  );
};
